import { APP_BASE, APP_HEADER } from "./app";

export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails | undefined;

  constructor(status: number, message: string, problem?: ProblemDetails) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.problem = problem;
  }
}

/**
 * The `code` of an error the client raises itself, before or instead of an answer from the API (S 2.8): the request never reached it (`offline`,
 * `network`), or a 2xx came back that is not the API's own (`not_api` – a proxy's or the web server's page in place of the stored record;
 * `empty_answer` – a 200 with nothing in it to a write, which the API never answers: it stores and returns the record, or says 204).
 * They travel as `ApiError` with status 0 (or the 2xx status), so every screen and the shared save helper report the cause.
 */
export const TRANSPORT_CODES = { offline: "offline", network: "network", notApi: "not_api", empty: "empty_answer" } as const;

const TRANSPORT_TEXT = {
  en: {
    offline: "This device is offline: nothing was sent, and nothing was saved.",
    network: "The server could not be reached: nothing was saved. Check the connection and try again.",
    not_api: "The answer did not come from the platform, so the save is not confirmed. Try again.",
    empty_answer: "The answer was empty, so the save is not confirmed. Reload to see what was stored before trying again.",
  },
  ar: {
    offline: "هذا الجهاز غير متصل بالشبكة: لم يرسل شيء ولم يحفظ شيء.",
    network: "تعذر الوصول إلى الخادم: لم يحفظ شيء. تحقق من الاتصال وأعد المحاولة.",
    not_api: "لم يصدر الرد عن المنصة، فلم يتأكد الحفظ. أعد المحاولة.",
    empty_answer: "جاء الرد فارغا، فلم يتأكد الحفظ. أعد تحميل الصفحة للتحقق مما حفظ قبل إعادة المحاولة.",
  },
} as const;

/** The window event raised after the API stored a request (a 2xx answer to a POST, PUT, PATCH or DELETE); `detail` is that request. */
export const STORED_EVENT = "ncp:stored";
export interface StoredRequest { method: string; path: string; body: unknown }

export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  errors?: Record<string, string[]>;
  traceId?: string;
  /** Minimal-API endpoints answer `{ error }` rather than a problem document. */
  error?: string;
  /** Machine-readable reason next to `error` (capability calls: forbidden, ungranted, limited, invalid, session…). */
  code?: string;
  /** Field-level problems (form and playbook-parameter validation). */
  issues?: Array<{ path: string; message: string; messageAr?: string | null; severity?: string }>;
}

export interface ApiClientOptions {
  baseUrl: string;
  getToken: () => string | null;
  /** A token refreshed first when it is about to expire (the session controller's `freshToken`); falls back to `getToken`. */
  getFreshToken?: () => Promise<string | null>;
  /** Renews the session after a `token_expired` 401; the request is then retried once. Resolves false when the session ended. */
  refresh?: () => Promise<boolean>;
  getLocale?: () => string;
  /** A 401 the client could not recover from, with the server's reason (`session_revoked`, `session_idle`, `session_expired`, …). */
  onUnauthorized?: (reason?: string | null) => void;
  /** The transport; pages pass the bridge's, which carries each call as a capability. Defaults to the browser's fetch. */
  fetch?: typeof fetch;
}

/** Why the server refused the bearer token: the problem `code`, or `token_expired` from the bearer challenge of an expired token. */
async function unauthorizedReason(res: Response): Promise<string | null> {
  try {
    const body = (await res.clone().json()) as { code?: string; error?: string } | null;
    if (body?.code) return body.code;
  } catch {
    // no JSON body (the bearer handler's own 401)
  }
  const challenge = res.headers.get("www-authenticate") ?? "";
  if (/invalid_token/i.test(challenge) && /expired/i.test(challenge)) return "token_expired";
  return null;
}

export type QueryParams = Record<string, string | number | boolean | null | undefined | Array<string | number>>;

function buildQuery(params?: QueryParams): string {
  if (!params) return "";
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v)) v.forEach((item) => sp.append(k, String(item)));
    else sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export class ApiClient {
  private readonly fetch: typeof fetch;

  constructor(private readonly options: ApiClientOptions) {
    this.fetch = options.fetch ?? ((input, init) => globalThis.fetch(input, init));
  }

  get baseUrl(): string {
    return this.options.baseUrl;
  }

  get<T>(path: string, params?: QueryParams, init?: RequestInit): Promise<T> {
    return this.request<T>("GET", `${path}${buildQuery(params)}`, undefined, init);
  }

  post<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
    return this.request<T>("POST", path, body, init);
  }

  put<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
    return this.request<T>("PUT", path, body, init);
  }

  patch<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
    return this.request<T>("PATCH", path, body, init);
  }

  delete<T>(path: string, init?: RequestInit): Promise<T> {
    return this.request<T>("DELETE", path, undefined, init);
  }

  async upload<T>(path: string, form: FormData, init?: RequestInit): Promise<T> {
    return this.request<T>("POST", path, form, init);
  }

  /** Fetches a binary resource with the bearer token, for showing in the page (an image, a PDF preview); null when there is none (404). */
  async blob(path: string): Promise<Blob | null> {
    const res = await this.send(path, {}, false);
    if (res.status === 404) return null;
    if (!res.ok) throw await this.toError(res);
    return res.blob();
  }

  /** Fetches a binary resource with the bearer token and triggers a browser download. */
  async download(path: string, fileName?: string): Promise<void> {
    const res = await this.send(path, {}, false);
    await this.saveAs(res, fileName);
  }

  /** Same as {@link download}, for endpoints that need a body to produce the document (a decision preview, for example). */
  async downloadPost(path: string, body: unknown, fileName?: string): Promise<void> {
    const res = await this.send(path, { method: "POST", body: JSON.stringify(body) }, true);
    await this.saveAs(res, fileName);
  }

  private async saveAs(res: Response, fileName?: string): Promise<void> {
    if (!res.ok) throw await this.toError(res);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName ?? res.headers.get("content-disposition")?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)/i)?.[1] ?? "download";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  /** A POST whose answer is binary (an Arrow table, a generated file), read whole with the bearer token. */
  async postBytes(path: string, body: unknown, signal?: AbortSignal, headers?: Record<string, string>): Promise<{ data: ArrayBuffer; type: string }> {
    const res = await this.send(path, { method: "POST", headers, body: JSON.stringify(body), signal }, true);
    if (!res.ok) throw await this.toError(res);
    return { data: await res.arrayBuffer(), type: res.headers.get("content-type") ?? "application/octet-stream" };
  }

  /** Opens a server-sent-events style streaming POST and yields decoded text chunks. */
  async *stream(path: string, body: unknown, signal?: AbortSignal, headers?: Record<string, string>): AsyncGenerator<string, void, void> {
    const res = await this.send(path, { method: "POST", headers, body: JSON.stringify(body), signal }, true);
    if (!res.ok || !res.body) throw await this.toError(res);
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      yield decoder.decode(value, { stream: true });
    }
  }

  /**
   * Same as {@link stream}, but reassembles the `data:` frames the API writes and yields each one parsed as JSON — the shape the
   * platform's streaming endpoints share: `{type: "token", text}` while the model writes, then one `{type: "final", …}` or `{type: "error", message}`.
   */
  async *streamEvents<T>(path: string, body: unknown, signal?: AbortSignal, headers?: Record<string, string>): AsyncGenerator<T, void, void> {
    let buffer = "";
    for await (const chunk of this.stream(path, body, signal, headers)) {
      buffer += chunk;
      let boundary = buffer.indexOf("\n\n");
      while (boundary >= 0) {
        const frame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        boundary = buffer.indexOf("\n\n");
        const data = frame.split("\n").filter((l) => l.startsWith("data:")).map((l) => l.slice(5).trim()).join("");
        if (data) yield JSON.parse(data) as T;
      }
    }
  }

  private async headers(json: boolean): Promise<Record<string, string>> {
    // The app's base path: apps that share a host (the back office and the console) keep their own sessions.
    const h: Record<string, string> = { Accept: "application/json", [APP_HEADER]: APP_BASE };
    if (json) h["Content-Type"] = "application/json";
    const token = this.options.getFreshToken ? await this.options.getFreshToken() : this.options.getToken();
    if (token) h.Authorization = `Bearer ${token}`;
    const locale = this.options.getLocale?.();
    if (locale) h["Accept-Language"] = locale;
    return h;
  }

  /**
   * Sends one request with the bearer token. A 401 for an expired access token (`token_expired`, or the bearer challenge saying so)
   * renews the session and retries once; any other 401 is handed to `onUnauthorized` with the server's reason.
   */
  private async send(path: string, init: RequestInit, json: boolean, retried = false): Promise<Response> {
    const extra = (init.headers ?? {}) as Record<string, string>;
    let res: Response;
    try {
      res = await this.fetch(`${this.options.baseUrl}${path}`, { ...init, headers: { ...(await this.headers(json)), ...extra } });
    } catch (e) {
      // A cancelled request stays a cancellation; anything else thrown by the transport means the request never reached the API.
      if (e instanceof DOMException && e.name === "AbortError") throw e;
      const code = typeof navigator !== "undefined" && navigator.onLine === false ? TRANSPORT_CODES.offline : TRANSPORT_CODES.network;
      throw new ApiError(0, this.transportText(code), { code, detail: e instanceof Error ? e.message : undefined });
    }
    if (res.status !== 401) return res;
    const reason = await unauthorizedReason(res);
    if (!retried && this.options.refresh && reason === "token_expired") {
      if (await this.options.refresh()) return this.send(path, init, json, true);
    }
    this.options.onUnauthorized?.(reason);
    return res;
  }

  private async request<T>(method: string, path: string, body?: unknown, init?: RequestInit): Promise<T> {
    const isForm = typeof FormData !== "undefined" && body instanceof FormData;
    const res = await this.send(
      path,
      { ...init, method, body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body) },
      !isForm && body !== undefined,
    );
    if (!res.ok) throw await this.toError(res);
    const writes = method !== "GET" && method !== "HEAD";
    if (res.status === 204) { if (writes) this.announceStored(method, path, body); return undefined as T; }
    const ct = res.headers.get("content-type") ?? "";
    // A web page in answer to a write is never the API's (the API answers JSON or nothing): a proxy or the site's own page stood in, so the
    // write is not confirmed and must not read as saved (S 2.8).
    if (writes && ct.includes("text/html")) throw new ApiError(res.status, this.transportText(TRANSPORT_CODES.notApi), { code: TRANSPORT_CODES.notApi });
    // An empty body is no answer to read: a 202 for queued work, as a 204. An empty 200 (or 201) to a write is not the API's – it answers a
    // stored write with the record, or with 204 when there is nothing to return – so the write is not confirmed (S 2.8).
    const text = await res.text();
    if (writes && text === "" && res.status !== 202) throw new ApiError(res.status, this.transportText(TRANSPORT_CODES.empty), { code: TRANSPORT_CODES.empty });
    const result = text === "" ? (undefined as T) : ct.includes("application/json") || ct.includes("application/problem+json") ? (JSON.parse(text) as T) : (text as unknown as T);
    if (writes && res.status !== 202) this.announceStored(method, path, body);
    return result;
  }

  /** Tells the page the API stored this request, so text kept on the device for it (`useDraft`) can be let go. */
  private announceStored(method: string, path: string, body: unknown): void {
    if (typeof window === "undefined" || typeof CustomEvent === "undefined") return;
    try { window.dispatchEvent(new CustomEvent<StoredRequest>(STORED_EVENT, { detail: { method, path, body } })); } catch { /* a listener's fault is not the request's */ }
  }

  private transportText(code: keyof typeof TRANSPORT_TEXT.en): string {
    return TRANSPORT_TEXT[this.options.getLocale?.()?.startsWith("ar") ? "ar" : "en"][code];
  }

  private async toError(res: Response): Promise<ApiError> {
    let problem: ProblemDetails | undefined;
    try {
      problem = (await res.clone().json()) as ProblemDetails;
    } catch {
      problem = undefined;
    }
    // A refused bearer token says why in its challenge only (no body): the reason travels as the code, so the cause can be named.
    if (res.status === 401 && !problem?.code) {
      const reason = await unauthorizedReason(res);
      if (reason) problem = { ...problem, code: reason };
    }
    return new ApiError(res.status, problem?.title ?? problem?.detail ?? problem?.error ?? `${res.status} ${res.statusText}`, problem);
  }
}
